import { createContext, useContext, useState, useEffect } from "react";
import { onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import { auth, googleProvider, yahooProvider } from "../firebase/config";
import imageCacheService from "../services/imageCacheService";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [justLoggedOut, setJustLoggedOut] = useState(false);

  // Helper function to process profile picture with caching
  const processProfilePicture = async (photoURL, userId, displayName) => {
    if (!photoURL) return null;

    try {
      const result = await imageCacheService.getCachedImage(photoURL, userId, displayName);
      if (result.success) {
        console.log(`📸 Profile image loaded from ${result.source}`);
        return result.imageData;
      } else {
        console.warn("Profile image failed to load, using fallback");
        return result.imageData; // Still return fallback image
      }
    } catch (error) {
      console.error("Error processing profile picture:", error);
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Process profile picture with caching
        const cachedPicture = await processProfilePicture(
          firebaseUser.photoURL,
          firebaseUser.uid,
          firebaseUser.displayName
        );

        const userProfile = {
          id: firebaseUser.uid,
          email: firebaseUser.email,
          name: firebaseUser.displayName,
          picture: cachedPicture || firebaseUser.photoURL,
          originalPhotoURL: firebaseUser.photoURL, // Keep original for reference
          provider: firebaseUser.providerData[0]?.providerId || "unknown",
          loginTime: new Date().toISOString(),
        };
        setUser(userProfile);
        setIsAuthenticated(true);
        setJustLoggedOut(false);
      } else {
        setUser(null);
        setIsAuthenticated(false);
        // Don't set justLoggedOut here as this handles Firebase state changes
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (provider) => {
    try {
      setIsLoading(true);
      let result;

      if (provider === "google") {
        result = await signInWithPopup(auth, googleProvider);
      } else if (provider === "yahoo") {
        result = await signInWithPopup(auth, yahooProvider);
      } else {
        throw new Error("Unsupported provider");
      }

      // Note: Profile picture processing is handled by onAuthStateChanged listener
      // to avoid duplicate API calls to Google servers

      const userProfile = {
        id: result.user.uid,
        email: result.user.email,
        name: result.user.displayName,
        picture: result.user.photoURL,
        originalPhotoURL: result.user.photoURL, // Keep original for reference
        provider: provider,
        loginTime: new Date().toISOString(),
      };

      localStorage.setItem("bibe_user", JSON.stringify(userProfile));
      // Note: setUser, setIsAuthenticated, setJustLoggedOut will be handled by onAuthStateChanged

      return { success: true, user: userProfile };
    } catch (error) {
      console.error(`${provider} login error:`, error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setIsAuthenticated(false);
      setJustLoggedOut(true);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    justLoggedOut,
    login,
    logout,
    clearLogoutState: () => setJustLoggedOut(false),
    imageCacheService, // Expose image cache service for manual operations
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};


export default AuthProvider;